import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import Home from '../Home';
import AboutBg from '../../img/about_hero_image.jpg';
import MissionImg from '../../img/vision.jpeg';
import VisionImg from '../../img/vision.jfif';
import Values from '../../img/values.png';
import Founder from '../../img/dfada.png';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    opacity: 0;
    transform: translateY(50px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Styled Components
const PageContainer = styled.div`
  font-family: 'Inter', 'Segoe UI', sans-serif;
  color: #2d3748;
  line-height: 1.6;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  min-height: 80vh;
  display: flex;
  align-items: center;
  position: relative;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: white;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url(${AboutBg}) center/cover no-repeat;
    opacity: 0.2;
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(30, 60, 114, 0.9) 0%, rgba(42, 82, 152, 0.8) 100%);
    z-index: 2;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 3;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
  animation: ${slideUp} 1s ease-out;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto 2rem;
  opacity: 0.9;
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  animation: ${float} 2s ease-in-out infinite;
  
  &::after {
    content: '↓';
    font-size: 1.5rem;
    color: white;
  }
`;

const Section = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #1e3c72, #2a5298);
    border-radius: 2px;
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutText = styled.div`
  animation: ${slideUp} 0.8s ease-out;
  
  p {
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
    color: #4a5568;
  }
`;

const AboutImage = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  animation: ${slideUp} 0.8s ease-out 0.2s both;
  
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.5s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(30, 60, 114, 0.2) 0%, rgba(42, 82, 152, 0.1) 100%);
    z-index: 1;
  }
`;

const StatsSection = styled(Section)`
  background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%);
  border-radius: 20px;
  margin: 4rem auto;
  max-width: 1100px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 2.5rem 1.5rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: ${slideUp} 0.6s ease-out both;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
  
  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }
  &:nth-child(5) { animation-delay: 0.5s; }
  &:nth-child(6) { animation-delay: 0.6s; }
`;

const StatNumber = styled.h3`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const StatLabel = styled.p`
  color: #718096;
  font-weight: 500;
  font-size: 1.1rem;
`;

const VMVSection = styled(Section)`
  background: white;
`;

const VMVGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const VMVCard = styled.div`
  background: ${props => props.highlight ? 'linear-gradient(135deg, #1e3c72, #2a5298)' : 'white'};
  color: ${props => props.highlight ? 'white' : 'inherit'};
  padding: 2.5rem 2rem;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: ${slideUp} 0.8s ease-out both;
  
  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const VMVIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.highlight 
    ? 'rgba(255, 255, 255, 0.2)' 
    : 'linear-gradient(135deg, #1e3c72, #2a5298)'};
  
  img {
    width: 40px;
    height: 40px;
    filter: ${props => props.highlight ? 'brightness(0) invert(1)' : 'none'};
  }
`;

const VMVTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const VMVText = styled.p`
  line-height: 1.7;
  opacity: ${props => props.highlight ? 0.9 : 0.8};
`;

const ReviewsSection = styled(Section)`
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: white;
  border-radius: 20px;
  margin: 4rem auto;
  max-width: 1100px;
`;

const ReviewsTitle = styled(SectionTitle)`
  color: white;
  
  &::after {
    background: linear-gradient(90deg, #ffffff, #a0aec0);
  }
`;

const ReviewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ReviewCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 2rem 1.5rem;
  border-radius: 12px;
  text-align: center;
  transition: transform 0.3s ease, background 0.3s ease;
  animation: ${slideUp} 0.6s ease-out both;
  
  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }
  
  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ReviewPlatform = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const ReviewStars = styled.p`
  font-size: 1.5rem;
  color: #f6e05e;
  margin: 0;
`;

const FounderSection = styled(Section)`
  background: #f8fafc;
  border-radius: 20px;
  margin: 4rem auto;
  max-width: 1100px;
`;

const FounderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const FounderImageContainer = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -15px;
    left: -15px;
    right: 15px;
    bottom: 15px;
    border: 3px solid #2a5298;
    border-radius: 12px;
    z-index: 0;
  }
`;

const FounderImg = styled.img`
  width: 100%;
  max-width: 350px;
  border-radius: 12px;
  position: relative;
  z-index: 1;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.5s ease;
  
  &:hover {
    transform: scale(1.03);
  }
`;

const FounderDetails = styled.div`
  animation: ${slideUp} 0.8s ease-out;
`;

const FounderRole = styled.h3`
  color: #2a5298;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const FounderName = styled.h4`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #1a202c;
`;

const FounderBio = styled.p`
  color: #4a5568;
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #1e3c72, #2a5298);
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(30, 60, 114, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(30, 60, 114, 0.4);
  }
`;

const SRKAbout = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <Home>
      <PageContainer>
        <HeroSection>
          <HeroContent>
            <HeroTitle>Transforming Businesses Through Digital Innovation</HeroTitle>
            <HeroSubtitle>
              We empower small and mid-size enterprises with cutting-edge digital solutions 
              that drive growth, enhance visibility, and maximize ROI.
            </HeroSubtitle>
            <CTAButton>Discover Our Solutions</CTAButton>
          </HeroContent>
          <ScrollIndicator />
        </HeroSection>

        <Section id="about">
          <SectionTitle>About SRK Softwares</SectionTitle>
          <AboutGrid>
            <AboutText>
              <p>
                SRK Softwares is a premier IT services and digital marketing company 
                dedicated to delivering innovative solutions that drive business growth. 
                Founded in 2020, we've grown from a small team of passionate professionals 
                into a trusted partner for businesses across diverse industries.
              </p>
              <p>
                Our expertise spans comprehensive 360° digital marketing services, 
                including Search Engine Optimization (SEO), content marketing, paid advertising, 
                social media management, and custom software development.
              </p>
              <p>
                With offices in Noida, Jaipur, and Patna, we serve clients across India, 
                providing tailored solutions that transform digital presence into powerful 
                tools for lead generation, customer engagement, and revenue growth.
              </p>
              <CTAButton>Learn More About Us</CTAButton>
            </AboutText>
            <AboutImage>
              <img src={AboutBg} alt="SRK Softwares Team" />
            </AboutImage>
          </AboutGrid>
        </Section>

        <StatsSection>
          <SectionTitle>Our Impact in Numbers</SectionTitle>
          <StatsGrid>
            <StatCard>
              <StatNumber>5+</StatNumber>
              <StatLabel>Years of Excellence</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>50+</StatNumber>
              <StatLabel>Projects Delivered</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>21+</StatNumber>
              <StatLabel>Talented Professionals</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>97%</StatNumber>
              <StatLabel>Client Satisfaction</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>4+</StatNumber>
              <StatLabel>States We Serve</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>24/6</StatNumber>
              <StatLabel>Operational Support</StatLabel>
            </StatCard>
          </StatsGrid>
        </StatsSection>

        <VMVSection>
          <SectionTitle>Our Vision, Mission & Values</SectionTitle>
          <VMVGrid>
            <VMVCard>
              <VMVIcon>
                <img src={VisionImg} alt="Vision" />
              </VMVIcon>
              <VMVTitle>Our Vision</VMVTitle>
              <VMVText>
                To be the leading digital solutions provider, empowering businesses 
                of all sizes to achieve their full potential in the digital landscape.
              </VMVText>
            </VMVCard>
            <VMVCard highlight={true}>
              <VMVIcon highlight={true}>
                <img src={MissionImg} alt="Mission" />
              </VMVIcon>
              <VMVTitle>Our Mission</VMVTitle>
              <VMVText highlight={true}>
                To deliver innovative, reliable, and user-centric solutions that solve 
                complex business challenges and create lasting value for our clients.
              </VMVText>
            </VMVCard>
            <VMVCard>
              <VMVIcon>
                <img src={Values} alt="Values" />
              </VMVIcon>
              <VMVTitle>Our Values</VMVTitle>
              <VMVText>
                We are committed to integrity, collaboration, and continuous improvement, 
                ensuring every project reflects our dedication to excellence and client success.
              </VMVText>
            </VMVCard>
          </VMVGrid>
        </VMVSection>

        <ReviewsSection>
          <ReviewsTitle>Trusted By Clients Across Platforms</ReviewsTitle>
          <ReviewsGrid>
            <ReviewCard>
              <ReviewPlatform>Facebook</ReviewPlatform>
              <ReviewStars>★★★★★</ReviewStars>
              <p>5.0 Rating</p>
            </ReviewCard>
            <ReviewCard>
              <ReviewPlatform>Google</ReviewPlatform>
              <ReviewStars>★★★★★</ReviewStars>
              <p>5.0 Rating</p>
            </ReviewCard>
            <ReviewCard>
              <ReviewPlatform>GoodFirms</ReviewPlatform>
              <ReviewStars>★★★★★</ReviewStars>
              <p>Top Rated</p>
            </ReviewCard>
            <ReviewCard>
              <ReviewPlatform>Clutch</ReviewPlatform>
              <ReviewStars>★★★★★</ReviewStars>
              <p>5.0 Rating</p>
            </ReviewCard>
          </ReviewsGrid>
        </ReviewsSection>

        <FounderSection>
          <SectionTitle>Our Leadership</SectionTitle>
          <FounderGrid>
            <FounderImageContainer>
              <FounderImg src={Founder} alt="Founder and CEO" />
            </FounderImageContainer>
            <FounderDetails>
              <FounderRole>Founder and CEO</FounderRole>
              <FounderName>SRK</FounderName>
              <FounderBio>
                Er. Ajay Kumar Thakur is a dynamic entrepreneur and the visionary behind 
                SRK Software Solutions. With a Bachelor's degree in Computer Science Engineering 
                from Siddaganga Institute Of Technology and over 6 years of industry experience, 
                he brings expertise in digital marketing, software engineering, and mobile app development.
              </FounderBio>
              <FounderBio>
                His passion for leveraging technology to solve real-world challenges has 
                helped numerous businesses enhance their digital presence, streamline operations, 
                and achieve measurable growth. Under his leadership, SRK Softwares continues 
                to deliver cutting-edge, tailored solutions that empower brands to thrive 
                in today's competitive digital landscape.
              </FounderBio>
              <CTAButton>Connect With Our Team</CTAButton>
            </FounderDetails>
          </FounderGrid>
        </FounderSection>
      </PageContainer>
    </Home>
  );
};

export default SRKAbout;